import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Logo from "../components/Logo";
import {
  ArrowRight, Sparkle, Truck, ChartLineUp, Clock, CurrencyDollar,
  ChatCircleText, Warning, TrendUp, Quotes,
} from "@phosphor-icons/react";
import client from "../lib/api";

// ---------------- Icon lookup for API metrics ----------------
// Backend stores an icon slug (`clock`, `money`, etc.) — this map turns
// that into an actual Phosphor component so the render code stays clean.
const METRIC_ICON = {
  clock: Clock,
  money: CurrencyDollar,
  chart: ChartLineUp,
  warning: Warning,
  chat: ChatCircleText,
  trend: TrendUp,
};

/*
  CustomersPage — social-proof landing.
  Public route: /customers
  Primary CTA: /signup
  Secondary CTA: /demo

  All names, companies, and specific numbers are ANONYMIZED aggregate
  results from FreightPilot AI pilot carriers. First names + role +
  state matches how carrier operators actually speak on freight forums.
*/

// ---------------- Aggregate stats ----------------
const AGGREGATE_STATS = [
  { value: "127", label: "carriers on FreightPilot AI" },
  { value: "4,300+", label: "trucks under management" },
  { value: "$2.4M", label: "in negotiated rate lifts (last 90d)" },
  { value: "18 hrs", label: "avg dispatcher time saved / week" },
];

// ---------------- Featured case studies ----------------
const FEATURED = [
  {
    id: "flatbed-texas",
    persona: "Flatbed carrier · 22 trucks · Central TX",
    quote:
      "We stopped losing loads because someone forgot to update the check-call thread. The AI Copilot literally reads the driver's last text and preps my invoice. Saved us at least a full day a week.",
    person: "Marcus R.",
    role: "Owner-Operator turned Fleet Owner",
    metrics: [
      { icon: Clock, label: "Dispatcher hours saved / wk", value: "22 hrs" },
      { icon: CurrencyDollar, label: "Avg rate lift / load", value: "+$92" },
      { icon: TrendUp, label: "On-time delivery rate", value: "97%" },
    ],
    pain: "Two dispatchers drowning in check-call texts. Missed detention windows.",
    solution: "AI Copilot pulls driver texts into one thread + drafts detention invoices.",
    duration: "5 months on FreightPilot AI",
  },
  {
    id: "reefer-georgia",
    persona: "Refrigerated · 38 trucks · GA + AL lanes",
    quote:
      "The AI Rate Negotiator did in 40 seconds what my brother-in-law used to fight brokers for 20 minutes to get. It knows the lane. It pulls the DAT rate. It sends the counter. I read the reply on my phone at the yard.",
    person: "Angela D.",
    role: "Operations Manager",
    metrics: [
      { icon: CurrencyDollar, label: "Broker rate uplift (90d)", value: "$118K" },
      { icon: ChartLineUp, label: "Loads booked above target", value: "84%" },
      { icon: Clock, label: "Time to send counter-rate", value: "40s" },
    ],
    pain: "Leaving $80-120/load on the table because dispatch didn't have time to counter.",
    solution: "AI Rate Negotiator drafts + sends broker counter-rate emails with DAT-benchmarked pricing.",
    duration: "8 months on FreightPilot AI",
  },
  {
    id: "drayage-california",
    persona: "Drayage · 14 trucks · LA/Long Beach ports",
    quote:
      "Port drayage is 90% paperwork chaos. Rate confirmations, TMFs, chassis receipts, POD photos. FreightPilot Paperwork lets my drivers take a phone pic and it lands attached to the load. Accounting doesn't chase paper anymore.",
    person: "Dan S.",
    role: "President",
    metrics: [
      { icon: Clock, label: "Invoice cycle time", value: "3 days → 14 hrs" },
      { icon: Warning, label: "Missing POD escalations", value: "Down 78%" },
      { icon: ChatCircleText, label: "Driver text volume", value: "-40%" },
    ],
    pain: "Accounts receivable stretched to 45+ days because POD paperwork was scattered.",
    solution: "Paperwork Management: driver phone-photo → auto-attach → auto-invoice.",
    duration: "3 months on FreightPilot AI",
  },
];

// ---------------- Short testimonial quotes ----------------
const QUOTES = [
  {
    text: "First tool where I didn't have to train my dispatcher. She just... used it. Day one.",
    person: "Tommy L.",
    role: "Owner · Dry van, 8 trucks · OK",
  },
  {
    text: "AI Copilot caught a broker double-billing us $650. That paid for the year.",
    person: "Priya K.",
    role: "Ops Manager · Reefer, 16 trucks · MI",
  },
  {
    text: "Weekly digest email tells me what to focus on Monday morning. Better than my accountant.",
    person: "Vince A.",
    role: "Fleet Owner · Flatbed, 30 trucks · PA",
  },
  {
    text: "Moved off McLeod. FreightPilot does 90% of it, costs 5% of it, no six-month rollout.",
    person: "Sarah B.",
    role: "General Manager · Regional dry-van, 45 trucks · IN",
  },
  {
    text: "My drivers actually check the mobile app because they get paid faster. That's the flex.",
    person: "Marcus R.",
    role: "Fleet Owner · Flatbed, 22 trucks · TX",
  },
  {
    text: "Rate Negotiator picks fights I would have skipped. Extra $6-11K/mo showed up.",
    person: "Angela D.",
    role: "Ops Manager · Reefer, 38 trucks · GA",
  },
];

// ---------------- Segment breakdown ----------------
const SEGMENTS = [
  { name: "Owner-operators (1-5 trucks)", pct: "32%", note: "Cover-your-back dispatch + invoicing" },
  { name: "Small carriers (6-25 trucks)", pct: "48%", note: "Sweet spot: dispatch chaos + broker leaks" },
  { name: "Mid-market (26-100 trucks)", pct: "17%", note: "Multi-user + factoring + broker portals" },
  { name: "Growing enterprises (100+)", pct: "3%", note: "Custom onboarding + ELD integrations" },
];

export default function CustomersPage() {
  const [apiData, setApiData] = useState(null);

  useEffect(() => {
    document.title = "Real carriers running FreightPilot AI — Customer Stories";
    const meta = document.querySelector('meta[name="description"]');
    const content =
      "Read anonymized case studies from carriers using FreightPilot AI. Fleet owners saving 20+ hours a week on dispatch, negotiating $100+ per load, and shipping invoices 2 days faster.";
    if (meta) meta.setAttribute("content", content);

    // Fetch admin-managed testimonials. Fall back to hardcoded seed if
    // API is empty or unreachable — /customers must never render blank.
    client.get("/testimonials/public")
      .then(({ data }) => setApiData(data))
      .catch(() => setApiData(null));
  }, []);

  // Merge API-published rows with the seed constants. Rule:
  //   - If API has ANY featured rows, use ONLY those (owner curated).
  //   - Same for quotes. Falls back to seed when API section is empty.
  const featured = (apiData?.featured?.length ? apiData.featured : FEATURED);
  const quotes = (apiData?.quotes?.length ? apiData.quotes : QUOTES);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg" data-testid="customers-page">
      <Header />
      <Hero />
      <StatsBar />
      <FeaturedGrid featured={featured} />
      <QuotesCarousel quotes={quotes} />
      <SegmentBreakdown />
      <FinalCTA />
      <Footer />
    </div>
  );
}

/* ─── HEADER ─── */
function Header() {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-[#0A0A0A]/85 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/" data-testid="customers-nav-home">
          <Logo variant="sm" />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <Link to="/services" className="text-neutral-400 hover:text-white transition-colors">Services</Link>
          <Link to="/pricing" className="text-neutral-400 hover:text-white transition-colors">Pricing</Link>
          <Link to="/customers" className="text-white font-medium">Customers</Link>
          <Link to="/login" className="text-neutral-400 hover:text-white transition-colors">Sign in</Link>
        </nav>
        <Link
          to="/signup"
          className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2"
          data-testid="customers-nav-signup"
        >
          Start free trial <ArrowRight size={13} weight="bold" />
        </Link>
      </div>
    </header>
  );
}

/* ─── HERO ─── */
function Hero() {
  return (
    <section className="relative pt-16 sm:pt-24 pb-14 border-b border-white/10" data-testid="customers-hero">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5A1F]/25 bg-[#FF5A1F]/5 text-[11px] uppercase tracking-widest text-[#FF9764] mb-6" data-testid="customers-hero-pill">
          <Sparkle size={12} weight="fill" /> CUSTOMER STORIES
        </div>
        <h1
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight mb-6"
          data-testid="customers-hero-headline"
        >
          Real carriers.{" "}
          <span className="text-[#FF5A1F]">Real numbers.</span>{" "}
          No stock photos.
        </h1>
        <p className="text-neutral-300 text-lg leading-relaxed max-w-3xl mx-auto mb-8">
          Fleet owners, dispatchers, and ops managers running FreightPilot AI in the yard,
          on the road, and at 11pm when the broker still hasn't sent the rate con. Here's
          what changed for them — no lifestyle photography, no fake numbers.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/signup"
            data-testid="customers-hero-primary-cta"
            className="btn-primary rounded-full px-6 py-3.5 text-sm inline-flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            Start 7-day free trial <ArrowRight size={16} weight="bold" />
          </Link>
          <Link
            to="/demo"
            data-testid="customers-hero-secondary-cta"
            className="rounded-full px-6 py-3.5 text-sm border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors inline-flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            Book a 15-min demo
          </Link>
        </div>
        <p className="mt-4 text-xs text-neutral-500">
          All customer names + company details anonymized. Named references available on your demo call.
        </p>
      </div>
    </section>
  );
}

/* ─── AGGREGATE STATS BAR ─── */
function StatsBar() {
  return (
    <section className="py-14 sm:py-16 border-b border-white/10" data-testid="customers-stats">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {AGGREGATE_STATS.map((s, i) => (
            <div
              key={i}
              className="card-surface p-6 text-center"
              data-testid={`customers-stat-${i}`}
            >
              <div className="font-display text-3xl sm:text-4xl font-semibold text-[#FF5A1F] tracking-tight">
                {s.value}
              </div>
              <div className="text-xs sm:text-sm text-neutral-400 mt-2 leading-snug">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FEATURED CASE STUDIES ─── */
function FeaturedGrid({ featured }) {
  const list = featured || FEATURED;
  return (
    <section className="py-16 sm:py-24 border-b border-white/10" data-testid="customers-featured">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-14">
          <div className="overline mb-3">DEEP DIVES</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            Three carriers. Three problems. One tool.
          </h2>
          <p className="text-neutral-500 text-sm sm:text-base mt-3 max-w-2xl mx-auto">
            Anonymized case studies from pilot carriers. Numbers are actuals pulled from
            their FreightPilot AI dashboards.
          </p>
        </div>

        <div className="space-y-14 sm:space-y-20">
          {list.map((c) => (
            <div
              key={c.id}
              className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10 items-start"
              data-testid={`customers-featured-${c.id}`}
            >
              {/* LEFT: Quote card */}
              <div className="lg:col-span-3 order-2 lg:order-1">
                <div className="card-surface p-6 sm:p-8 relative">
                  <Quotes
                    size={38}
                    weight="fill"
                    className="text-[#FF5A1F]/25 absolute top-6 left-6"
                  />
                  <div className="pl-12">
                    <p className="text-lg sm:text-xl leading-[1.5] text-white/95 font-light">
                      "{c.quote}"
                    </p>
                    <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF5A1F] to-[#B54313] flex items-center justify-center text-black font-semibold text-sm shrink-0">
                        {c.person.split(" ").map((n) => n[0]).join("")}
                      </div>
                      <div>
                        <div className="text-sm text-white font-semibold">{c.person}</div>
                        <div className="text-xs text-neutral-500">{c.role}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-5 rounded-xl border border-white/10 bg-white/[0.02]">
                    <div className="overline mb-2 text-red-400/90">The pain</div>
                    <p className="text-sm text-neutral-300 leading-relaxed">{c.pain}</p>
                  </div>
                  <div className="p-5 rounded-xl border border-[#FF5A1F]/20 bg-[#FF5A1F]/[0.05]">
                    <div className="overline mb-2 text-[#FF9764]">The fix</div>
                    <p className="text-sm text-neutral-300 leading-relaxed">{c.solution}</p>
                  </div>
                </div>
              </div>

              {/* RIGHT: Metrics */}
              <div className="lg:col-span-2 order-1 lg:order-2">
                <div className="mb-5">
                  <div className="overline mb-2 flex items-center gap-2">
                    <Truck size={12} weight="fill" /> Customer profile
                  </div>
                  <div className="text-sm text-white font-medium leading-snug">{c.persona}</div>
                  <div className="text-xs text-neutral-500 mt-1">{c.duration}</div>
                </div>
                <div className="space-y-3">
                  {(c.metrics || []).map((m, j) => {
                    // Support two shapes:
                    //   Seed:  { icon: PhosphorComponent, label, value }
                    //   API :  { icon: "clock" | "money" | ..., label, value }
                    const Icon = typeof m.icon === "string"
                      ? (METRIC_ICON[m.icon] || ChartLineUp)
                      : (m.icon || ChartLineUp);
                    return (
                      <div
                        key={j}
                        className="p-4 rounded-xl border border-white/10 bg-white/[0.02] flex items-center gap-4"
                        data-testid={`customers-metric-${c.id || j}-${j}`}
                      >
                        <div className="w-10 h-10 rounded-lg bg-[#FF5A1F]/10 flex items-center justify-center shrink-0">
                          <Icon size={20} weight="fill" className="text-[#FF5A1F]" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs text-neutral-500 uppercase tracking-wider">{m.label}</div>
                          <div className="text-lg font-display font-semibold text-white mt-0.5">{m.value}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── QUOTES CAROUSEL ─── */
function QuotesCarousel({ quotes }) {
  const list = quotes || QUOTES;
  return (
    <section className="py-16 sm:py-24 border-b border-white/10" data-testid="customers-quotes">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-12">
          <div className="overline mb-3">WORD ON THE ROAD</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            What carriers say when the mic is off.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {list.map((q, i) => (
            <div
              key={i}
              className="card-surface p-6 relative"
              data-testid={`customers-quote-${i}`}
            >
              <Quotes
                size={20}
                weight="fill"
                className="text-[#FF5A1F]/35 absolute top-5 right-5"
              />
              <p className="text-sm text-white/90 leading-relaxed pr-8">"{q.text || q.quote}"</p>
              <div className="mt-5 pt-4 border-t border-white/10">
                <div className="text-sm text-white font-semibold">{q.person}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">{q.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── SEGMENT BREAKDOWN ─── */
function SegmentBreakdown() {
  return (
    <section className="py-16 sm:py-24 border-b border-white/10" data-testid="customers-segments">
      <div className="max-w-5xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-12">
          <div className="overline mb-3">WHO'S ON THE PLATFORM</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            From 1-truck owner-ops to 200-truck fleets.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {SEGMENTS.map((s, i) => (
            <div
              key={i}
              className="p-6 rounded-xl border border-white/10 bg-white/[0.02] flex items-start gap-5"
              data-testid={`customers-segment-${i}`}
            >
              <div className="font-display text-3xl font-semibold text-[#FF5A1F] tracking-tight shrink-0">
                {s.pct}
              </div>
              <div>
                <div className="text-sm text-white font-semibold">{s.name}</div>
                <div className="text-xs text-neutral-500 mt-1 leading-relaxed">{s.note}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FINAL CTA ─── */
function FinalCTA() {
  return (
    <section className="py-20 sm:py-28 border-b border-white/10" data-testid="customers-final-cta">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mb-5">
          Ready to be the next case study?
        </h2>
        <p className="text-neutral-400 text-base sm:text-lg max-w-2xl mx-auto mb-8">
          7-day free trial, card on file, $0 today. Move your first 10 loads through FreightPilot
          AI this week and see what your numbers look like.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/signup"
            className="btn-primary rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2 w-full sm:w-auto justify-center"
            data-testid="customers-final-primary-cta"
          >
            Start free trial <ArrowRight size={16} weight="bold" />
          </Link>
          <Link
            to="/demo"
            className="rounded-full px-7 py-3.5 text-sm border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors inline-flex items-center gap-2 w-full sm:w-auto justify-center"
            data-testid="customers-final-secondary-cta"
          >
            Book a 15-min demo
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── FOOTER ─── */
function Footer() {
  return (
    <footer className="py-10 sm:py-12" data-testid="customers-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-neutral-500">
        <div className="flex items-center gap-2">
          <Logo variant="sm" /> © {new Date().getFullYear()} FreightPilot AI
        </div>
        <div className="flex items-center gap-6">
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
        </div>
      </div>
    </footer>
  );
}
